import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';

import CustomToolbar from 'components/CustomToolbar/CustomToolbar';
import StatusButton from 'components/SalesOrders/StatusButton';
import ProductStatusButton from 'components/SalesOrders/ProductStatusButton';
import MeasurementsModal from 'components/SalesOrders/MeasurementsModal';

import { setNewSOInfo, updateSalesOrderStatus, deleteSalesOrder } from 'redux/actions';
import { standard } from 'constants/userAccountTypes';
import { Ready, Delivered, Payment, Completed } from 'constants/status';

const SalesOrdersTable = (props) => {

  const [rowsSelected, setRowsSelected] = useState([]);
  const [rowsExpanded, setRowsExpanded] = useState([]);

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openMeasureModal, setOpenMeasureModal] = useState(false);
  const [measureModalInput, setMeasureModalInput] = useState("");
  
  //const [statusFilter, setStatusFilter] = useState([]);

  const filteredSalesOrders = useMemo(() => {
    //if the user is standard, only that user's SalesOrders will be shown,
    //else all SalesOrders will be shown.
    if(props.activeUserType === standard) return props.salesOrders;
    else return props.salesOrders.filter(salesOrder => salesOrder.userId === props.user.standardUsers[props.userTabValue].id);

  }, [props.salesOrders, props.userTabValue, props.activeUserType, props.user]);

  const tableData = useMemo(() => {
    const initialTableData = [];
    
    for ( let i = 0; i < filteredSalesOrders.length; i++ ) {
      const salesOrder = filteredSalesOrders[i];
      const salesOrderCustomer = props.customers.find(customer => customer.id === salesOrder.customerId);
      initialTableData.push({
        id: salesOrder.id,
        displayId: salesOrder.displayId,
        name: salesOrderCustomer ? salesOrderCustomer.name : "",
        phone: salesOrderCustomer ? salesOrderCustomer.phone : "",
        startDate: moment(salesOrder.startDate).format('DD/MM/YYYY'),
        endDate: moment(salesOrder.endDate).format('DD/MM/YYYY'),
        products: salesOrder.products,
        total: salesOrder.total,
        status: salesOrder.status
      });
    }

    return initialTableData;

  }, [filteredSalesOrders, props.customers]);

  const onRowSelectionChange = (curr, all, rowsSelected) => {
    setRowsSelected(rowsSelected);
  };

  const onRowExpansionChange = (currentRowsExpanded, allRowsExpanded) => {
    setRowsExpanded(allRowsExpanded.map(row => row.dataIndex));
  };

  const customToolbarSelect = () => {
    return (
      <CustomToolbar
        switchy={0}
        handleModalOpen={handleCancelModalOpen}
      />
    )
  };

  const updateSalesOrderPayments = (salesOrderInfo) => {
    //UseWhatsapp("+917354699444", "Hello!!");
    const salesOrderProducts = salesOrderInfo.products.map(salesOrderProduct => {
      const productInfo = props.products.find(product => product.id === salesOrderProduct.productId);
      return {
        name: productInfo ? productInfo.name : "",
        company: productInfo ? productInfo.company : "",
        ...salesOrderProduct
      }
    });
    props.setNewSOInfo({
      displayId: salesOrderInfo.displayId,
      customerId: salesOrderInfo.customerId,
      soProducts: salesOrderProducts,
      startDate: salesOrderInfo.startDate, 
      endDate: salesOrderInfo.endDate,
      status: Payment,
      total: salesOrderInfo.total,
      payments: salesOrderInfo.payments,
      activeStep: 0,
      update: false
    });
    
    props.setOpenPaymentModal(true);
  };

  const checkProductStatusReady = (salesOrderId, soProductId) => {

    const salesOrderInfo = filteredSalesOrders.find(salesOrder => salesOrder.id === salesOrderId);
    // Filter out the current 'Ready' product and any other 'Ready' products.
    const otherProducts = salesOrderInfo.products.filter(product => (product.id !== soProductId) && (product.status !== Ready));

    // If this is the only product OR If every other product status is also 'Ready', set SO status to 'Ready'.
    if (otherProducts.length === 0) props.updateSalesOrderStatus({ id: salesOrderId, status: Ready }, props.user.adminId);
  };

  const checkProductStatusDelivered = (salesOrderId, soProductId) => {

    const salesOrderInfo = filteredSalesOrders.find(salesOrder => salesOrder.id === salesOrderId);
    // Filter out the current 'Delivered' product.
    const otherProducts = salesOrderInfo.products.filter(product => product.id !== soProductId);

    //checks if every other product status is also 'Delivered', or this is the only product.
    if (otherProducts.length > 0) {
      var deliveredCount = 0;
      var nonDeliveredCount = 0;
      for (let i = 0; i < otherProducts.length; i++) {
        if (otherProducts[i].status === Delivered) deliveredCount += 1;
        else nonDeliveredCount += 1;
      }

      //checks if all product status are 'Delivered' and payments have been made, if yes then sets SO status to 'Completed' or else sets it to 'Payment'.
      if (deliveredCount === 0) props.updateSalesOrderStatus({ id: salesOrderId, status: Payment }, props.user.adminId);
      else if (nonDeliveredCount === 0 && salesOrderInfo.paymentDate) props.updateSalesOrderStatus({ id: salesOrderId, status: Completed }, props.user.adminId);
    }
    else {
      if (salesOrderInfo.paymentDate) props.updateSalesOrderStatus({ id: salesOrderId, status: Completed }, props.user.adminId);
      else props.updateSalesOrderStatus({ id: salesOrderId, status: Payment }, props.user.adminId);
    }
  };

  const columns = [
    { label: "ID", name: "id", options: { display: 'excluded', filter: false, sort: false, viewColumns: false } },
    { label: "Order ID", name: "displayId", options: { filter: false, sort: false, viewColumns: false } },
    { label: "Customer Name", name: "name", options: { filter: true, sort: true } },
    { label: "Customer Phone", name: "phone", options: { filter: false, sort: false } },
    { label: "Start Date", name: "startDate", options: { filter: true, sort: true } },
    { label: "End Date", name: "endDate", options: { filter: true, sort: true } },
    { label: "Total Amount", name: "total", options: { filter: false, sort: false } },
    {
      label: "Status",
      name: "status",
      options: {
        filter: true,
        sort: true,
        viewColumns: false,
        //filterList: statusFilter,
        customBodyRender: (value, tableMeta) => (
          <StatusButton
            isStandard={props.activeUserType === standard}
            status={value}
            salesOrderInfo={filteredSalesOrders.find(salesOrder => salesOrder.id === tableMeta.rowData[0])}
            updateSalesOrderPayments={updateSalesOrderPayments}
          />
        )
      }
    }
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsSelected,
    rowsExpanded,
    onRowSelectionChange,
    onRowExpansionChange,
    customToolbarSelect,
    selectableRows: props.activeUserType === standard ? 'single' : 'none',
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    isRowExpandable: (dataIndex, expandedRows) => {
      // Prevent expand/collapse of any row if there are 3 rows expanded already (but allow those already expanded to be collapsed)
      if (expandedRows.data.length > 3 && expandedRows.data.filter(d => d.dataIndex === dataIndex).length === 0)
        return false;
      return true;
    },
    renderExpandableRow: (rowData, rowMeta) => {
      const colSpan = rowData.length + 1;
      const salesOrderId = rowData[0];
      const rowProducts = tableData.find(salesOrder => salesOrder.id === salesOrderId).products;
      return (
        <tr>
          <td colSpan={colSpan}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Product Brand</TableCell>
                    <TableCell>Selling Price</TableCell>
                    <TableCell>Product Status</TableCell>
                    <TableCell>Measurements</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowProducts.map((rowProduct) => {
                    const productInfo = props.products.find(product => product.id === rowProduct.productId);
                    return (
                      <TableRow key={rowProduct.displayId}>
                        <TableCell>
                          {rowProduct.displayId}
                        </TableCell>
                        <TableCell>{productInfo ? productInfo.name : ""}</TableCell>
                        <TableCell>{productInfo ? productInfo.company : ""}</TableCell>
                        <TableCell>{rowProduct.sellPrice}</TableCell>
                        <TableCell>
                          <ProductStatusButton
                            isStandard={props.activeUserType === standard}
                            soProduct={{
                              salesOrderId: salesOrderId,
                              id: rowProduct.id,
                              status: rowProduct.status
                            }}
                            user={props.user}
                            checkProductStatusReady={checkProductStatusReady}
                            checkProductStatusDelivered={checkProductStatusDelivered}
                          />
                        </TableCell>
                        <TableCell>
                          { rowProduct.measurements ?
                          <Button
                            color="primary"
                            onClick={handleMeasureModalOpen.bind(this, rowProduct.measurements)}
                          >
                            View
                          </Button> : null }
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </td>
        </tr>
      );
    }
  };

  /*const UseWhatsapp = (mobileNumber, message) => {
    let number = mobileNumber.replace(/[^\w\s]/gi, "").replace(/ /g, "");
  
    let url = `https://web.whatsapp.com/send?phone=${number}`;
  
    url += `&text=${encodeURI(message)}&app_absent=0`;
  
    window.open(url);
  };*/

  const handleMeasureModalOpen = (measurements) => {
    setMeasureModalInput(measurements);
    setOpenMeasureModal(true);
  };

  const handleMeasureModalClose = () => {
    setMeasureModalInput("");
    setOpenMeasureModal(false);
  };

  const handleCancelModalOpen = () => {
    setOpenCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setOpenCancelModal(false);
    setRowsSelected([]);
  };

  const cancelSalesOrder = (id) => {
    props.deleteSalesOrder(id, props.user.adminId);

    setOpenCancelModal(false);
    setRowsSelected([]);
  };

  const renderCancelModal = () => {
    const salesOrderInfo = filteredSalesOrders[rowsSelected[0]];
    const startDate = moment(salesOrderInfo.startDate);
    const cancelValid = startDate.isSameOrAfter(moment().subtract(3, 'day'), 'day');

    return (
      <Modal
        open={openCancelModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20%'
        }}
      >
        { cancelValid ?
          <Card>
            <CardHeader
              title="Cancel Sales Order ?"
            />
            <Divider />
            <CardContent>
              <p> The Following Sales Order will be Cancelled... </p>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={cancelSalesOrder.bind(this, salesOrderInfo.id)}
                >
                  Cancel
                </Button>
                <Button sx={{ mx: 1 }} onClick={handleCancelModalClose}>
                  Close
                </Button>
              </Box>
            </CardContent>
          </Card> :
          <Card>
            <CardHeader
              title="Cannot Cancel Sales Order."
            />
            <Divider />
            <CardContent>
              <p> The Sales Order was created more than 3 days ago. <br/>
              It cannot be cancelled anymore... </p>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Button sx={{ mx: 1 }} onClick={handleCancelModalClose}>
                  Close
                </Button>
              </Box>
            </CardContent>
          </Card>
        }
      </Modal>
    )
  };

  return (
    <React.Fragment>
      <MUIDataTable
        title={"Sales Orders"}
        data={tableData}
        columns={columns}
        options={options}
      />
      { openMeasureModal ?
      <MeasurementsModal
        open={openMeasureModal}
        edit={false}
        measureInput={measureModalInput}
        closeModal={handleMeasureModalClose}
      /> : null }
      { openCancelModal ? renderCancelModal() : null }
    </React.Fragment>
  )
};

export default connect (null, { setNewSOInfo, updateSalesOrderStatus, deleteSalesOrder }) (SalesOrdersTable);